import qs from 'qs';
import { httpClient, qsOptions } from 'providers/clients/httpClient';
import {
  DynamicFieldType,
  FieldAccessConfig,
} from 'components/DynamicFormField/DynamicFormField';
import { hasValue } from 'utility/functions';
import { TAbortCtrlSignal } from 'utility/types/dataProvider';

/**
 * ---------------------------------------------
 * Access Control Types
 * ---------------------------------------------
 * 10/30/2025 - openapi-ts generated types seems
 * incorrect at the moment for some of these
 * due to the openapi schema being invalid,
 * manually defining the types until fixed.
 */

// Access Control Rule Type
type AccessControlRuleType = 'web' | 'amp' | 'app' | string;

// Create/Update body for an Access Rule
// components['schemas']['access.rule_request_save']
export type AccessRuleRequestSave = {
  name: string;
  method_group_id: string;
  offer_group_id?: string;
  type: AccessControlRuleType;
  enabled?: boolean;
  dismiss_url?: string;
  message_id?: string;
  priority?: number;
  keywords?: string[];
  criteria?: {
    id: string;
    enabled: boolean;
    negate: boolean;
  }[];
  enable_multivariate?: boolean;
  notification_email?: string;
  test_start_date?: string;
  test_end_date?: string;
  offer_group_b_id?: string;
  offer_group_c_id?: string;
  offer_group_d_id?: string;
};

/**
 * Partial - Criteria Item
 * - data within a AccessRuleItem
 *
 * This is data within the AccessRuleItem
 * under criteria property, we generally
 * use this for the criteria modal form
 * where the only important props there
 * are id, type, and app.
 *
 * The rest of the data is primarily used
 * for the journey access criteria list.
 *
 */
export type AccessRuleCriteriaItem = {
  id?: string;
  type?: AccessCriteriaType;
  app?: AccessCriteriaApp;
  name?: string;
  enabled?: boolean;
  negate?: boolean;
};

// An existing Access Rule eg from /access/rules/{id}/
// components['schemas']['access.rule_retrieve']
export type AccessRuleItem = {
  id?: string;
  name?: string;
  type?: string;
  enabled?: boolean;
  priority?: number;
  message_id?: string;
  method_group?: {
    id?: string;
    name?: string;
    settings?: any[];
  };
  offer_group?: {
    id?: string;
    name?: string;
  };
  criteria?: AccessRuleCriteriaItem[];
  keywords?: string[];
  dismiss_url?: string;
  last_updated?: string;
  enable_multivariate?: boolean;
  notification_email?: string;
  test_start_date?: string;
  test_end_date?: string;
  offer_group_b?: {
    id?: string;
    name?: string;
  };
  offer_group_c?: {
    id?: string;
    name?: string;
  };
  offer_group_d?: {
    id?: string;
    name?: string;
  };
};

// /access/rules/ POST (body) (access.rule_request_save)
export type CreateAccessRuleParams = {
  body: AccessRuleRequestSave;
};

// /access/rules/ POST response
export type CreateAccessRuleResult = {
  status: string | number;
  data: AccessRuleItem;
};

// Get /access/criteria/ request query params
export type GetAccessCriteriaParams = {
  query?: string;
  page?: string | number;
  limit?: number;
};

// single item in the /access/criteria/ response
export type AccessCriteriaSearchItem = {
  id: string;
  name: string;
  last_updated: string;
  type: AccessCriteriaType;
  app: AccessCriteriaApp;
};

// /access/criteria/ response
export type GetAccessCriteriaResult = {
  status: string | number;
  data: {
    total: number;
    next: string | number;
    prev: string | number;
    items: AccessCriteriaSearchItem[];
  };
};

/**
 * GET /access/criteria/types/
 *
 * Note: renamed 'rule_type[]' to 'rule_type' for easier usage
 * for utilizing qs to stringify array[] qp params, the api
 * expects qp in form: ?rule_type[]=amp&driver_name[]=web
 */
export type GetAccessCriteriaTypesParams = {
  query?: string;
  rule_type?: AccessControlRuleType[];
  show_all?: boolean;
  page?: string;
  limit?: number;
};

// Single item - via /access/criteria/types/
export type AccessCriteriaTypeSearchItem = {
  type: AccessControlRuleType;
  label: string;
  access?: FieldAccessConfig;
};

// GET /access/criteria/types/ response
export type GetAccessCriteriaTypesResult = {
  status: string | number;
  data: {
    total: number;
    next: string | number;
    prev: string | number;
    items: AccessCriteriaTypeSearchItem[];
  };
};

/**
 * Access Criteria Types and Configurations
 * ref: components['schemas']['access.criteria']
 */
export type AccessCriteriaType =
  | 'adblock'
  | 'appman'
  | 'archivetime'
  | 'asseteditortoggle'
  | 'assetflag'
  | 'assettype'
  | 'custom'
  | 'dmp'
  | 'gdpr'
  | 'hostname'
  | 'incognito'
  | 'ip'
  | 'keyword'
  | 'metatag'
  | 'metercheck'
  | 'queryparams'
  | 'referrer'
  | 'starttime'
  | 'subscriberstatus'
  | 'url'
  | 'urlpattern'
  | 'user'
  | 'useragent'
  | string;

/** Access Criteria Type - App Options */
type AccessCriteriaApp = 'editorial' | 'eedition' | string;

type AdblockCriteriaConfig = {
  restrict?: boolean;
};

type AppmanCriteriaConfig = {
  profile_id?: string;
  profile_name?: string;
};

type ArchiveTimeCriteriaConfig = {
  app?: string;
  archived?: number;
};

type AssetEditorToggleCriteriaConfig = {
  access?: string[];
};

type AssetFlagCriteriaConfig = {
  assetflag?: string[];
};

type AssetTypeCriteriaConfig = {
  app?: string;
  assettype?: string[];
};

type CustomJSCriteriaConfig = {
  javascript?: string;
};

type GDPRCriteriaConfig = {
  restrict?: boolean;
};

type HostnameCriteriaConfig = {
  host?: string[];
};

type IncognitoCriteriaConfig = {
  restrict?: boolean;
};

type IpAddrCriteriaConfig = {
  ip?: string[];
};

type KeywordCriteriaConfig = {
  keyword?: string[];
};

type MetaTagCriteriaConfig = {
  meta?: {
    name?: string;
    value?: string;
  }[];
};

type MeterCheckCriteriaConfig = {
  op?: string;
  /** Format: uuid */
  meter?: string;
  views?: string;
};

export type QPCriteriaEntry = {
  name?: string;
  match?: string;
  value?: string;
  preserve?: number;
};

type QueryParamsCriteriaConfig = {
  queryparams?: QPCriteriaEntry[];
};

type ReferrerCriteriaConfig = {
  referrer?: {
    name?: string;
  }[];
};

type StartTimeCriteriaConfig = {
  /** eg: 'editorial',  'eedition' */
  app?: string;
  /** like "1 hours ago" */
  starttime?: string;
};

type UrlCriteriaConfig = {
  url?: string[];
  url_exclude?: string[];
};

type UrlPatternCriteriaConfig = {
  pattern?: string[];
};

type UserAgentCriteriaConfig = {
  useragent?: string[];
};

type UserConfigCriteriaConfig = {
  login_required?: '0' | '1' | string;
};

// AKA "CDP" Audiences
type DMPItem = { id?: string; abbr?: string; name?: string };
type DMPCriteriaConfig = {
  dmp?: DMPItem[];
};

// Service ID, Service Name
type SubStatusItem = { id?: number; name?: string };
type SubStatusCriteriaConfig = {
  status?: string;
  max_age?: string;
  services?: SubStatusItem[];
};

/**
 * Unified type for all criteria configs.
 * One set is a valid config for a given criteria type.
 * Rendering is handled by dynamic fields components.
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type AccessCriteriaConfig =
  | AdblockCriteriaConfig
  | AppmanCriteriaConfig
  | ArchiveTimeCriteriaConfig
  | AssetEditorToggleCriteriaConfig
  | AssetFlagCriteriaConfig
  | AssetTypeCriteriaConfig
  | CustomJSCriteriaConfig
  | DMPCriteriaConfig
  | GDPRCriteriaConfig
  | HostnameCriteriaConfig
  | IncognitoCriteriaConfig
  | IpAddrCriteriaConfig
  | KeywordCriteriaConfig
  | MetaTagCriteriaConfig
  | MeterCheckCriteriaConfig
  | QueryParamsCriteriaConfig
  | ReferrerCriteriaConfig
  | StartTimeCriteriaConfig
  | SubStatusCriteriaConfig
  | UrlCriteriaConfig
  | UrlPatternCriteriaConfig
  | UserConfigCriteriaConfig
  | UserAgentCriteriaConfig;

/**
 * This simpler unified type is just for ts typehints
 * note that in reality only one of the actual property
 * combinations (AccessCriteriaConfig) is a valid set.
 *
 * IMPORTANT: Certain fields that are denum do NOT save
 * back to the API 1:1 with the GET response shape.
 * So, you CANNOT post back the full object as-is.
 * For example 'dmp', 'services' these will expect
 * a string[] of IDs instead of the object array.
 */
export type CriteriaConfigProperties = {
  app?: string;
  restrict?: boolean;
  profile_id?: string;
  profile_name?: string;
  archived?: number;
  starttime?: string;
  access?: string[];
  assetflag?: string[];
  assettype?: string[];
  javascript?: string;
  host?: string[];
  ip?: string[];
  keyword?: string[];
  pattern?: string[];
  login_required?: '0' | '1' | string;
  useragent?: string[];
  url?: string[];
  url_exclude?: string[];
  op?: string;
  meter?: string;
  views?: string;
  status?: string;
  max_age?: string;
  meta?: { name?: string; value?: string }[]; // metatag 'group'
  queryparams?: { name?: string; match?: string; value?: string; preserve?: number }[];
  referrer?: { name?: string }[];
  // These are NOT 1:1 with the GET response shape.
  dmp?: DMPItem[] | string[];
  services?: SubStatusItem[] | string[];
  [key: string]: any;
};

/**
 *
 * GET /access/criteria/types/{id}/ args
 *
 * path.id
 * - criteria type (eg: adblock, metercheck, etc..)
 * query.app
 * - filter by app type to disambiguate
 * - eg: starttime criteria may have varying
 *   configs based on app (editorial vs eedition)
 *
 * Dynamic fields spec and related code
 * https://townnews.atlassian.net/wiki/spaces/COSMOS/pages/2881847298/
 * typegen: components['schemas']['core.field.spec.field']
 * UI: /src/components/DynamicFormField/...
 *
 **/

// GET /access/criteria/types/{id}/ args
export type GetAccessCriteriaTypeConfigParams = {
  query?: { app?: string };
  path?: { id: string };
};

// Single entry in the /access/criteria/types/{id}/ response
export type AccessCriteriaTypeConfig = {
  label: string;
  config: DynamicFieldType[];
};

/**
 * GET /access/criteria/types/{id}/ response
 * data is an object, not an array.
 */
export type GetAccessCriteriaTypeConfigResult = {
  status: string | number;
  data: AccessCriteriaTypeConfig;
};

/**
 * GET Full Item - /access/criteria/{id}/
 * Config should always be object, not array.
 * Talk to Daniel if API returns anything else.
 */
export type AccessCriteriaItem = {
  id?: string;
  name?: string;
  last_updated?: string;
  type?: AccessCriteriaType;
  config?: CriteriaConfigProperties;
};

// GET /access/criteria/{id}/
export type GetAccessCriteriaByIdParams = {
  path: { id: string };
};

// GET /access/criteria/{id}/ response
export type GetAccessCriteriaByIdResult = {
  status: string | number;
  data?: AccessCriteriaItem;
};

// PUT /access/criteria/{id}/ args
export type UpdateAccessCriteriaParams = {
  path: { id: string };
  body: {
    name: string;
    config: CriteriaConfigProperties;
  };
};

// PUT /access/criteria/{id}/ response
export type UpdateAccessCriteriaResult = {
  status: string | number;
  data: AccessCriteriaItem;
};

// POST /access/criteria/ args
export type CreateAccessCriteriaParams = {
  body: {
    name: string;
    type: string;
    config: CriteriaConfigProperties;
  };
};

// POST  /access/criteria/ response
export type CreateAccessCriteriaResult = {
  status: string | number;
  data: AccessCriteriaItem;
};

// DELETE /access/criteria/{id}/ args
export type DeleteAccessCriteriaParams = {
  path?: { id?: string };
};

// DELETE /access/criteria/{id}/ success  (204 No Content)
export type DeleteAccessCriteriaResult = {
  status?: string | number;
  [key: string]: any;
};

/**
 * Offer Groups and Offers - /access/offers/...
 *
 * Shape of an existing offer within an OfferGroupItem
 * eg: resp.offers[] from /access/offers/{id}/
 */
export type OfferGroupOfferItem = {
  id?: string;
  service_id?: number;
  title?: string;
  description?: string;
  /** Access name */
  image?: string;
  image_url?: string;
  rate?: string;
  rate_data?: {
    id?: string;
    name?: string;
    amount?: number;
    duration?: number;
  };
  selected_promo?: string;
  link_service?: string;
  limits?: string;
  billing_cycle?: string;
  savings?: string;
  /** In legacy this is a checkbox to enable featured_message */
  featured?: boolean;
  featured_message?: string;
  button_text?: string;
  link_url?: string;
  link_js?: string;
  enabled?: boolean;
  start_date?: string;
  end_date?: string;
  /**
   * no start date & no end date
   * has start date or has end date
   */
  display_range?: boolean[];
  /**
   * no link url & no link js & no service link
   * no link url
   * no link js
   */
  link?: boolean[];
  linked?: boolean;
  external_id?: string;
};

/**
 * Single offer within create/update BODY
 * for offer group request save or update.
 *
 * NOTE:
 * If ID is provided, this is the id of an existing offer to update.
 * eg: updating an existing offer within the offers array in-place.
 * If ID is is NOT provided, a new offer will be created.
 * This is the case during creating or adding a new offer.
 */
export type CreateUpdateOfferGroupOffersItem = {
  /**
   * ID if updating offer, none when create/add new offer
   */
  id?: string;
  service_id?: number;
  title?: string;
  description?: string;
  /** Access name */
  image?: string;
  rate?: string;
  rate_id?: string;
  selected_promo?: string;
  link_service?: string;
  limits?: string;
  billing_cycle?: string;
  savings?: string;
  /** In legacy this is a checkbox to enable featured_message */
  featured?: boolean;
  featured_message?: string;
  button_text?: string;
  link_url?: string;
  /** JS code */
  link_js?: string;
  enabled?: boolean;
  start_date?: string;
  end_date?: string;
};

export type OfferGroupTemplates = {
  url_map_id?: string;
  block_template?: string;
};

// Create body for an Offer Group
// components['schemas']['access.offer_group_request_save']
export type OfferGroupRequestSaveBody = {
  name?: string;
  title?: string;
  redirect_url?: string;
  message?: string;
  offers?: CreateUpdateOfferGroupOffersItem[];
  templates?: OfferGroupTemplates[];
};

// Update body for an Offer Group
// components['schemas']['access.offer_group_request_update']
export type OfferGroupRequestUpdateBody = {
  name?: string;
  title?: string;
  redirect_url?: string;
  message?: string;
  offers?: CreateUpdateOfferGroupOffersItem[];
  templates?: OfferGroupTemplates[];
};

// An existing Offer Group Item
// components['schemas']['access.offer_group']
export type OfferGroupItem = {
  id?: string;
  name?: string;
  title?: string;
  redirect_url?: string;
  message?: string;
  offers?: OfferGroupOfferItem[];
  templates?: OfferGroupTemplates[];
};

// post (create) /access/offers/ request
export type CreateOfferGroupParams = {
  body: OfferGroupRequestSaveBody;
};

// post (create) /access/offers/ response
export type CreateOfferGroupResult = {
  status: string | number;
  data: OfferGroupItem;
};

// put (update) /access/offers/{id}/
export type UpdateOfferGroupParams = {
  path: { id: string };
  body: OfferGroupRequestUpdateBody;
};

// put (update) /access/offers/{id}/ response
export type UpdateOfferGroupResult = {
  status: string | number;
  data: OfferGroupItem;
};

// delete /access/offers/{id}/
export type DeleteOfferGroupParams = {
  path: { id: string };
};

// delete /access/offers/{id} response (204 No Content)
export type DeleteOfferGroupResult = {
  status?: string | number;
  [key: string]: any;
};

// get search /access/offers/ request qp
export type GetOfferGroupsParams = {
  query?: string;
  page?: string | number;
  limit?: number;
};

// get search /access/offers/ success
export type GetOfferGroupsResult = {
  status: string | number;
  data: {
    total: number;
    next: string | number;
    prev: string | number;
    items: OfferGroupItem[];
  };
};

// get /access/offers/{id}/ request
export type GetOfferGroupByIdParams = {
  path: { id: string };
};

// get /access/offers/{id}/ response
export type GetOfferGroupByIdResult = {
  status: string | number;
  data: OfferGroupItem;
};

// Single item in the /access/methods/ search response
export type AccessMethodSearchItem = {
  id?: string;
  name?: string;
  methods?: {
    id?: string;
    name?: string;
    type?: string;
  }[];
  settings?: {
    wall_behavior?: 'nag' | 'default';
    [key: string]: any;
  };
};

// GET /access/methods/ request
export type GetAccessMethodsParams = {
  query?: string;
  page?: string | number;
  limit?: number;
};

// Search /access/methods/ success
export type GetAccessMethodsResult = {
  status: string | number;
  data: {
    total: number;
    next: string | number;
    prev: string | number;
    items: AccessMethodSearchItem[];
  };
};

// Access Method Metadata
export type MethodMetaJS = { js?: string };

type MethodMetaMeterMsg = {
  id?: string;
  message?: string;
  show_at?: string;
};

export type MethodMetaMeter = {
  meter?: number;
  messages?: MethodMetaMeterMsg[];
};

export type MethodMetaSubscription = {
  service_id?: number;
  service_info?: {
    driver?: string;
    description?: string;
    delivery_required?: number;
    help?: string;
    hidden?: boolean;
    disabled?: boolean;
    testmode?: number;
    linked?: number;
    priority?: number;
    active_users?: number;
    purchase_url?: string;
    graphic?: any[] | Record<string, any>;
  };
};

// Some dont have metadata
export type MethodMetaFree = null;
export type MethodMetaUser = null;
export type MethodMetaMailingList = null;

// Combined object types grouped together
type TMethodMetaObject = MethodMetaJS & MethodMetaMeter & MethodMetaSubscription;

// Combined types for RHF typing (object properties or null)
export type MethodMetadata =
  | TMethodMetaObject
  | MethodMetaFree
  | MethodMetaUser
  | MethodMetaMailingList;

export type MethodType =
  | 'customjavascript'
  | 'mailinglist'
  | 'meter'
  | 'subscription'
  | 'free'
  | 'user'
  | string;

export type AccessMethod = {
  id?: string;
  name?: string;
  type?: MethodType;
  metadata?: MethodMetadata;
};

type AccessMethodSettings = {
  wall_behavior?: 'nag' | 'default' | string;
};

// Full Access Method Item 'access.method'
// /access/methods/{id}/
export type AccessMethodItem = {
  id?: string;
  name?: string;
  settings?: AccessMethodSettings;
  methods?: AccessMethod[];
};

// POST/PUT Body for /access/methods/ and /{id}/
// NOTE: Methods is just array of string ids
export type UpdateAccessMethodItem = {
  name?: string;
  settings?: AccessMethodSettings;
  methods?: string[];
};

// Single Item via GET /access/methods/providers/
export type AccessMethodProviderItem = {
  type?: string;
  label?: string;
};

// GET /access/methods/providers/
// no args currently
export type GetAccessMethodProvidersArgs = {};

export type GetAccessMethodProvidersResult = {
  status: string | number;
  data: AccessMethodProviderItem[];
};

// GET /access/methods/providers/{id}/
// NOTE: ID is TYPE (eg: 'customjavascript', 'meter', etc..)
export type GetAccessMethodProviderByIdArgs = {
  path: { id: string };
};

export type GetAccessMethodProviderByIdResult = {
  status: string | number;
  data: AccessMethod[];
};

// POST (create) /access/methods/
export type PostAccessMethodParams = {
  body: UpdateAccessMethodItem;
};

// POST (create) /access/methods/ response
export type PostAccessMethodResult = {
  status: string | number;
  data: AccessMethodItem;
};

// PUT /access/methods/{id}/ args
export type PutAccessMethodIdParams = {
  path: { id: string };
  body: UpdateAccessMethodItem;
};

// PUT /access/methods/{id}/ response
export type PutAccessMethodIdResult = {
  status: string | number;
  data: AccessMethodItem;
};

// GET /access/methods/{id}/ args
export type GetAccessMethodByIdParams = {
  path: { id: string };
};

// GET /access/methods/{id}/ response
export type GetAccessMethodByIdResult = {
  status: string | number;
  data: AccessMethodItem;
};

// DELETE /access/methods/{id}/ args
export type DeleteAccessMethodByIdParams = {
  path: { id: string };
};

// DELETE /access/methods/{id}/ response (204 No Content)
export type DeleteAccessMethodByIdResult = {
  status?: string | number;
  [key: string]: any;
};

// search /access/rules/ request query params
export type GetAccessRulesParams = {
  type?: AccessControlRuleType;
  query?: string;
  is_enabled?: 'true' | 'false' | string;
  rate_id?: string; // filter to connected rate_id used by offers
  method_group_id?: string; // filter to connected method groups
  dir?: 'asc' | 'desc' | string;
  sort?: 'name' | 'priority' | string;
  page?: string | number;
  limit?: string | number;
};

export type AccessRuleSearchItem = {
  id?: string;
  type?: AccessControlRuleType;
  enabled?: boolean;
  /** format: datetime */
  last_updated?: string;
  priority?: number;
  name?: string;
  method?: string;
  offer?: string;
  method_config?: string;
  criteria?: string;
};

// /access/rules/ response
export type GetAccessRulesResult = {
  status: string | number;
  data: {
    total: number;
    next: string | number | null;
    prev: string | number | null;
    items: AccessRuleSearchItem[];
  };
};

// /access/rules/{id}/ request
export type GetAccessRuleByIdParams = {
  path: { id: string };
};

// /access/rules/{id}/ response
export type GetAccessRuleByIdResult = {
  status: string | number;
  data: AccessRuleItem;
};

// /access/rules/{id}/ update args
export type UpdateAccessRuleParams = {
  path: { id: string };
  body: AccessRuleRequestSave;
};

// /access/rules/{id}/ POST response
export type UpdateAccessRuleResult = {
  status: string | number;
  data: AccessRuleItem;
};

// /access/rules/{id}/ DELETE args (path)
export type DeleteAccessRuleParams = {
  path: { id: string };
};

// /access/rules/{id}/ DELETE response (204)
export type DeleteAccessRuleResult = {
  status?: string | number;
  [key: string]: any;
};

// Generate HTML Preview for an offer group with specific template
// POST /access/offers/{id}/preview/{map_id}/ (path + body)
export type GetOfferGroupPreviewParams = {
  path: {
    /** The unique ID of the offer group to preview */
    id: string;
    /** The URL map ID to use for template resolution */
    map_id: string;
  };
  body?: OfferGroupRequestSaveBody;
  // AbortSignal can be passed to cancel the request
  signal?: TAbortCtrlSignal;
};

export type GetOfferGroupPreviewResult = {
  status: string | number;
  /** html preview string */
  data: string;
};

/**
 * -----------------------------------------------
 * Access Control Provider
 * -----------------------------------------------
 */
export const accessControlProvider = {
  // get /access/offers/ (search offer groups)
  getOfferGroups: async (
    params?: GetOfferGroupsParams,
  ): Promise<GetOfferGroupsResult> => {
    try {
      const path = `/access/offers/`;

      const { query, page, limit = 50 } = params || {};

      const payload = {
        ...(query && { query: query }),
        ...(page && { page: page }),
        ...(limit && { limit: limit }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      const result: GetOfferGroupsResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getOfferGroups] Error:', error);
      throw error;
    }
  },

  // get /access/offers/{id}/ (get offer group by id)
  getOfferGroupById: async (
    params: GetOfferGroupByIdParams,
  ): Promise<GetOfferGroupByIdResult> => {
    try {
      const id = params?.path?.id;

      if (!id) {
        throw new Error('Missing required parameter!');
      }

      const path = `/access/offers/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetOfferGroupByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getOfferGroupById] Error:', error);
      throw error;
    }
  },

  // post /access/offers/ (create)
  createOfferGroup: async (
    params: CreateOfferGroupParams,
  ): Promise<CreateOfferGroupResult> => {
    try {
      const body = params?.body;

      if (!body) {
        throw new Error('Missing request body!');
      }

      const path = `/access/offers/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });

      const result: CreateOfferGroupResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[createOfferGroup] Error:', error);
      throw error;
    }
  },

  // put /access/offers/{id}/ (update offer group)
  updateOfferGroup: async (
    params: UpdateOfferGroupParams,
  ): Promise<UpdateOfferGroupResult> => {
    try {
      const id = params?.path?.id;
      const body = params?.body;

      if (!id) {
        throw new Error('Missing required path parameter.');
      }

      if (!body) {
        throw new Error('Missing request body.');
      }

      const path = `/access/offers/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });

      const result: UpdateOfferGroupResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[updateOfferGroup] Error:', error);
      throw error;
    }
  },

  // delete /access/offers/{id}/ (delete offer group)
  deleteOfferGroup: async (
    params: DeleteOfferGroupParams,
  ): Promise<DeleteOfferGroupResult> => {
    try {
      const id = params?.path?.id;

      if (!id) {
        throw new Error('Missing required parameter: id');
      }
      const path = `/access/offers/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      // Status code 204 if successful
      const result: DeleteOfferGroupResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteOfferGroup] Error:', error);
      throw error;
    }
  },

  // generate HTML preview for offer group
  // POST /access/offers/{id}/preview/{map_id}/
  getOfferGroupPreview: async (
    params: GetOfferGroupPreviewParams,
  ): Promise<GetOfferGroupPreviewResult> => {
    try {
      const id = params?.path?.id;
      const map_id = params?.path?.map_id;
      const body = params?.body;
      const abortSignal = params?.signal;

      /**
       * body seems to be optional?
       * - when provided, seems data sent will be used to generate the preview
       * - if not passed, uses existing offer group data to generate the preview
       */
      const ogBody = Boolean(body) ? JSON.stringify(body) : undefined;

      if (!id || !map_id) {
        throw new Error('Missing required path parameters.');
      }

      const path = `/access/offers/${id}/preview/${map_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: ogBody,
        signal: abortSignal,
      });

      const result: GetOfferGroupPreviewResult = {
        status: data?.status || status,
        data: data, // html string
      };

      return result;
    } catch (error) {
      console.error('[getOfferGroupPreview] Error:', error);
      throw error;
    }
  },

  // GET /access/criteria/types/ (search criteria types)
  getAccessCriteriaTypes: async (
    params?: GetAccessCriteriaTypesParams,
  ): Promise<GetAccessCriteriaTypesResult> => {
    try {
      const path = `/access/criteria/types/`;

      const queryArgs = params || {};
      const { query, rule_type, show_all, page, limit = 50 } = queryArgs;

      // Allows for passing eg: true/false/empty
      // to get all, or or only ones user has access to
      const hasShowAllArg = hasValue(show_all);

      const payload = {
        ...(query && { query: query }),
        ...(rule_type && { rule_type: rule_type }),
        ...(hasShowAllArg && { show_all: show_all }),
        ...(page && { page: page }),
        ...(limit && { limit: limit }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      const result: GetAccessCriteriaTypesResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getAccessCriteriaTypes] Error', error);
      throw error;
    }
  },

  // GET /access/criteria/types/{id}/ (get criteria type config by id)
  getAccessCriteriaTypeConfig: async (
    params: GetAccessCriteriaTypeConfigParams,
  ): Promise<GetAccessCriteriaTypeConfigResult> => {
    try {
      const id = params?.path?.id;

      if (!id) {
        throw new Error('Missing required path parameter: id');
      }

      const queryArgs = params?.query || {};
      const { app } = queryArgs;

      const payload = {
        ...(app && { app: app }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const path = `/access/criteria/types/${id}/`;
      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      const result: GetAccessCriteriaTypeConfigResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getAccessCriteriaTypeConfig] Error', error);
      throw error;
    }
  },

  // GET /access/criteria/ (search criteria)
  getAccessCriteria: async (
    params?: GetAccessCriteriaParams,
  ): Promise<GetAccessCriteriaResult> => {
    try {
      const path = `/access/criteria/`;

      const { query, page, limit = 50 } = params || {};

      const payload = {
        ...(query && { query: query }),
        ...(page && { page: page }),
        ...(limit && { limit: limit }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      const result: GetAccessCriteriaResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getAccessCriteria] Error', error);
      throw error;
    }
  },

  // GET /access/criteria/{id}/ (get criteria by id)
  getAccessCriteriaById: async (
    params: GetAccessCriteriaByIdParams,
  ): Promise<GetAccessCriteriaByIdResult> => {
    try {
      const id = params?.path?.id;

      if (!id) {
        throw new Error('Missing id parameter.');
      }

      const path = `/access/criteria/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetAccessCriteriaByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getAccessCriteriaById] Error', error);
      throw error;
    }
  },

  // PUT /access/criteria/{id}/ (update criteria)
  updateAccessCriteria: async (
    params: UpdateAccessCriteriaParams,
  ): Promise<UpdateAccessCriteriaResult> => {
    try {
      const body = params?.body;
      const id = params?.path?.id;
      if (!id) {
        throw new Error('Missing id parameter.');
      }
      const path = `/access/criteria/${id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });
      const result: UpdateAccessCriteriaResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[updateAccessCriteria] Error', error);
      throw error;
    }
  },

  // POST /access/criteria/ (create criteria)
  createAccessCriteria: async (
    params: CreateAccessCriteriaParams,
  ): Promise<CreateAccessCriteriaResult> => {
    try {
      const path = `/access/criteria/`;
      const body = params?.body;
      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });
      const result: CreateAccessCriteriaResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[createAccessCriteria] Error', error);
      throw error;
    }
  },

  // DELETE /access/criteria/{id}/ (delete criteria)
  deleteAccessCriteria: async (
    params: DeleteAccessCriteriaParams,
  ): Promise<DeleteAccessCriteriaResult> => {
    try {
      const id = params?.path?.id;
      if (!id) {
        throw new Error('Missing id parameter.');
      }
      const path = `/access/criteria/${id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });
      // Success is 204 No Content
      const result: DeleteAccessCriteriaResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[deleteAccessCriteria] Error', error);
      throw error;
    }
  },

  // GET /access/methods/
  getAccessMethods: async (
    params?: GetAccessMethodsParams,
  ): Promise<GetAccessMethodsResult> => {
    try {
      const path = `/access/methods/`;

      const { query, page, limit = 50 } = params || {};

      const payload = {
        ...(query && { query: query }),
        ...(page && { page: page }),
        ...(limit && { limit: limit }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${path}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      const result: GetAccessMethodsResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getAccessMethods] Error', error);
      throw error;
    }
  },

  // GET /access/methods/providers/
  getAccessMethodProviders: async (
    params?: GetAccessMethodProvidersArgs,
  ): Promise<GetAccessMethodProvidersResult> => {
    try {
      const path = `/access/methods/providers/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });
      const result: GetAccessMethodProvidersResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getAccessMethodProviders] Error', error);
      throw error;
    }
  },

  // GET /access/methods/providers/{id}/
  getAccessMethodProviderById: async (
    params: GetAccessMethodProviderByIdArgs,
  ): Promise<GetAccessMethodProviderByIdResult> => {
    try {
      const id = params?.path?.id;
      if (!id) {
        throw new Error('Missing id parameter.');
      }
      const path = `/access/methods/providers/${id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });
      const result: GetAccessMethodProviderByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getAccessMethodProviderById] Error', error);
      throw error;
    }
  },

  // GET /access/methods/{id}/
  getAccessMethodById: async (
    params: GetAccessMethodByIdParams,
  ): Promise<GetAccessMethodByIdResult> => {
    try {
      const id = params?.path?.id;
      if (!id) {
        throw new Error('Missing id parameter.');
      }
      const path = `/access/methods/${id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });
      const result: GetAccessMethodByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getAccessMethodById] Error', error);
      throw error;
    }
  },

  // POST /access/methods/ (create access method)
  postAccessMethod: async (
    params: PostAccessMethodParams,
  ): Promise<PostAccessMethodResult> => {
    try {
      const path = `/access/methods/`;
      const body = params?.body;
      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });
      const result: PostAccessMethodResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[postAccessMethod] Error', error);
      throw error;
    }
  },

  // PUT /access/methods/{id}/
  putAccessMethodById: async (
    params: PutAccessMethodIdParams,
  ): Promise<PutAccessMethodIdResult> => {
    try {
      const id = params?.path?.id;
      const body = params?.body;
      if (!id) {
        throw new Error('Missing id parameter.');
      }
      const path = `/access/methods/${id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });
      const result: PutAccessMethodIdResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[putAccessMethodById] Error', error);
      throw error;
    }
  },

  // DELETE /access/methods/{id}/
  deleteAccessMethodById: async (
    params: DeleteAccessMethodByIdParams,
  ): Promise<DeleteAccessMethodByIdResult> => {
    try {
      const id = params?.path?.id;
      if (!id) {
        throw new Error('Missing id parameter.');
      }
      const path = `/access/methods/${id}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });
      // Success is 204 No Content
      const result: DeleteAccessMethodByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[deleteAccessMethodById] Error', error);
      throw error;
    }
  },

  // GET (search) /access/rules/
  getAccessRules: async (
    params?: GetAccessRulesParams,
  ): Promise<GetAccessRulesResult> => {
    try {
      const basePath = `/access/rules/`;

      // Destructure params
      const queryArgs = params || {};
      const {
        type,
        query,
        is_enabled,
        rate_id,
        method_group_id,
        dir,
        sort,
        page,
        limit = 50,
      } = queryArgs;

      // Allows for passing: true/false/empty
      // to get all, only enabled, or only disabled rules.
      const hasIsEnabledArg = hasValue(is_enabled);

      const payload = {
        ...(type && { type: type }),
        ...(query && { query: query }),
        ...(rate_id && { rate_id: rate_id }),
        ...(method_group_id && { method_group_id: method_group_id }),
        ...(hasIsEnabledArg && { is_enabled: is_enabled }),
        ...(dir && { dir: dir }),
        ...(sort && { sort: sort }),
        ...(page && { page: page }),
        ...(limit && { limit: limit }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${basePath}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
        method: 'GET',
      });

      const result: GetAccessRulesResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getAccessRules] Error', error);
      throw error;
    }
  },

  // get /access/rules/{id}/
  getAccessRuleById: async (
    params: GetAccessRuleByIdParams,
  ): Promise<GetAccessRuleByIdResult> => {
    try {
      const id = params?.path?.id;

      if (!id) {
        throw new Error('Missing id parameter.');
      }

      const path = `/access/rules/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetAccessRuleByIdResult = {
        status: data?.status || status,
        data: data?.data,
        ...data,
      };

      return result;
    } catch (error) {
      console.error('[getAccessRuleById] Error', error);
      throw error;
    }
  },

  // post /access/rules/ (create)
  createAccessRule: async (
    params: CreateAccessRuleParams,
  ): Promise<CreateAccessRuleResult> => {
    try {
      const path = `/access/rules/`;

      const body = params?.body;
      if (!body) {
        throw new Error('Missing request body!');
      }

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });

      const result: CreateAccessRuleResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[createAccessRule] Error', error);
      throw error;
    }
  },

  // put /access/rules/{id}/ (update rule)
  updateAccessRule: async (
    params: UpdateAccessRuleParams,
  ): Promise<UpdateAccessRuleResult> => {
    try {
      const id = params?.path?.id;
      const body = params?.body;

      if (!id) {
        throw new Error('Missing required parameter: id');
      }

      if (!body) {
        throw new Error('Missing request body.');
      }

      const path = `/access/rules/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
      });

      const result: UpdateAccessRuleResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[updateAccessRule] Error', error);
      throw error;
    }
  },

  // delete /access/rules/{id}/ (delete rule)
  deleteAccessRule: async (
    params: DeleteAccessRuleParams,
  ): Promise<DeleteAccessRuleResult> => {
    try {
      const id = params?.path?.id;

      if (!id) {
        throw new Error('Missing required parameter: id');
      }

      const path = `/access/rules/${id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      // Status code 204 if successful, not in json.status
      const result: DeleteAccessRuleResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteAccessRule] Error', error);
      throw error;
    }
  },

  // as const - all keys (functions) in this object are readonly
} as const;
// Since all functions have their own signature with types
// we can safely use typeof helper to create the type for us
// and then '&' add it to the BloxCustomDataProvider type
export type AccessControlProviderType = typeof accessControlProvider;
